import { useCallback, useEffect, useRef } from 'react';
import {
  useLocation,
  useNavigate,
  type NavigateFunction,
  type NavigateOptions,
  type To,
} from 'react-router-dom';
import { captureScrollBeforeNavigation } from './useScrollRestoration';
import { getNavigationTransitionType, resolveTargetPath } from '../lib/navigation';
import { runViewTransition } from '../lib/viewTransition';

export const useTransitionNavigate = (): NavigateFunction => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      const fromPath = locationRef.current;
      captureScrollBeforeNavigation(fromPath);

      if (typeof to === 'number') {
        const type = to < 0 ? 'backward' : 'forward';
        runViewTransition(() => navigate(to), type);
        return;
      }

      const toPath = resolveTargetPath(to, fromPath);
      const type = getNavigationTransitionType(fromPath, toPath, 'PUSH');
      runViewTransition(() => navigate(to, options), type);
    },
    [navigate],
  ) as NavigateFunction;
};
